import React, { useState, useEffect } from "react";
import "../styles/ManageMember.css";
import SideBar from "../components/SideBar";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useLocation, useNavigate } from "react-router-dom";
import { memberService } from "../services/MemberServices";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function ManageMember() {
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("all");
  const [status, setStatus] = useState("all");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [filteredUsersCount, setFilteredUsersCount] = useState(0);
  const [currentPageUsersCount, setCurrentPageUsersCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state && location.state.message) {
      setSnackMessage(location.state.message);
      setSnackOpen(true);
    }
  }, [location.state]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, country, status]);

  // Fetch total member count on component mount and when filters change
  useEffect(() => {
    fetchTotalMemberCount();
  }, [search, status, country]);

  // Fetch members when filters change
  useEffect(() => {
    fetchMembers();
  }, [currentPage, search, status]);

  // Calculate filtered members count whenever members change
  useEffect(() => {
    if (members.length > 0) {
      const formattedMembers = formatMembers(members);
      
      // Filter members based on all criteria
      const filtered = formattedMembers.filter((member) => {
        const searchTerm = search.toLowerCase();
        
        const searchMatch =
          (member.id && member.id.toString().toLowerCase().includes(searchTerm)) ||
          member.fullName.toLowerCase().includes(searchTerm) ||
          member.email.toLowerCase().includes(searchTerm) ||
          member.firstName.toLowerCase().includes(searchTerm) ||
          member.surname.toLowerCase().includes(searchTerm);

        const countryMatch = country === "all" || 
          (member.address.country && member.address.country.toLowerCase() === country.toLowerCase());
        
        const statusMatch = status === "all" || 
          member.status.toLowerCase() === status.toLowerCase();

        return searchMatch && statusMatch && countryMatch;
      });

      // Calculate paginated members
      const itemsPerPage = 10;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

      // Update statistics
      setFilteredUsersCount(filtered.length);
      setCurrentPageUsersCount(paginated.length);
    } else {
      setFilteredUsersCount(0);
      setCurrentPageUsersCount(0);
    }
  }, [members, currentPage]);

  // Function to fetch total member count with filters
  const fetchTotalMemberCount = async () => {
    try {
      // Prepare filters for the count API
      const filters = {};
      
      if (search) {
        filters.search = search;
      }
      
      if (status !== 'all') {
        filters.status = status;
      }
      
      // Note: Country filter might need special handling since it's client-side filtered
      // The API might not support country filtering, so we'll exclude it from the count request
      
      const response = await memberService.getMemberCount(filters);
      
      // Assuming the API returns { count: number } or similar structure
      // Adjust based on your actual API response
      if (response.count !== undefined) {
        setTotalUsers(response.count);
      } else if (response.total !== undefined) {
        setTotalUsers(response.total);
      } else {
        // If the response structure is different, try to extract count from the first property
        const count = Object.values(response)[0];
        if (typeof count === 'number') {
          setTotalUsers(count);
        } else {
          console.warn('Unexpected API response format:', response);
          // Fallback: fetch count from pagination in fetchMembers
        }
      }
    } catch (error) {
      console.error('Error fetching member count:', error);
      // Don't show error to user for count failure, use fallback
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 10,
        ...(status !== 'all' && { status }),
        ...(search && { search })
      };

      const response = await memberService.getAllmembers(params);
      
      if (response.users && Array.isArray(response.users)) {
        setMembers(response.users);
        
        // Extract unique countries from the response
        const countries = [...new Set(response.users
          .map(member => member.country)
          .filter(country => country && country.trim() !== '')
        )].sort();
        
        setAvailableCountries(countries);
        
        // Use pagination total from API as fallback if getMemberCount failed
        if (response.pagination && response.pagination.total && totalUsers === 0) {
          setTotalUsers(response.pagination.total);
        }
      } else {
        setMembers([]);
        setAvailableCountries([]);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
      setSnackMessage('Failed to load members');
      setSnackOpen(true);
      setMembers([]);
      setAvailableCountries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackClose = () => setSnackOpen(false);

  const goToCreateAccount = () => {
    navigate("/create-member-account");
  };

  // Format API data to match your existing structure
  const formatMembers = (apiMembers) => {
    return apiMembers.map(member => ({
      id: member.Customer_id,
      fullName: `${member.first_name} ${member.last_name}`,
      firstName: member.first_name,
      surname: member.last_name,
      email: member.email,
      mobile: member.phone_number,
      address: {
        line1: member.address_line1 || '',
        line2: member.address_line2 || '',
        line3: member.address_line3 || '',
        city: member.city || '',
        county: member.region || 'N/A',
        country: member.country || 'N/A',
        postcode: member.postcode || ''
      },
      status: member.current_status ? member.current_status.charAt(0).toUpperCase() + member.current_status.slice(1) : 'Awaiting',
      notes: member.status_note || '',
      value: `${member.wallet_details?.total_points || 0} TopCoin`,
      wallet_details: member.wallet_details,
      transaction_summary: member.transaction_summary,
      recent_transactions: member.recent_transactions || []
    }));
  };

  const handleRowClick = (member) => {
    navigate("/member-details", { state: { member } });
  };

  // Use formatted members from API, fallback to empty array
  const formattedMembers = members.length > 0 ? formatMembers(members) : [];
  
  // Enhanced search logic for ID, name, and email
  const filteredMembers = formattedMembers.filter((member) => {
    const searchTerm = search.toLowerCase();
    
    const searchMatch =
      (member.id && member.id.toString().toLowerCase().includes(searchTerm)) ||
      member.fullName.toLowerCase().includes(searchTerm) ||
      member.email.toLowerCase().includes(searchTerm) ||
      member.firstName.toLowerCase().includes(searchTerm) ||
      member.surname.toLowerCase().includes(searchTerm);

    const countryMatch = country === "all" || 
      (member.address.country && member.address.country.toLowerCase() === country.toLowerCase());
    
    const statusMatch = status === "all" || 
      member.status.toLowerCase() === status.toLowerCase();

    return searchMatch && statusMatch && countryMatch;
  });

  const itemsPerPage = 10;
  const totalPages = Math.ceil((totalUsers || 0) / itemsPerPage);
  const paginatedMembers = filteredMembers; // Already paginated from API

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{border:0, boxShadow:"none"}}>
          <div className="header-top">
            <h2 className="header-title">Manage Member</h2>
            <button className="action-btn" onClick={goToCreateAccount}>+ Add New Member</button>
          </div>

          {/* User Statistics Section */}
          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Members:</span>
              <span className="stat-value">{totalUsers}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered Members:</span>
              <span className="stat-value">{filteredUsersCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page:</span>
              <span className="stat-value">
                {paginatedMembers.length}
              </span>
            </div>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Filter by country:</label>
              <select value={country} onChange={(e) => setCountry(e.target.value)} className="filter-select">
                <option value="all">All Countries</option>
                {availableCountries.map((countryName, index) => (
                  <option key={index} value={countryName.toLowerCase()}>
                    {countryName}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Filter by status:</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="filter-select">
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="awaiting">Awaiting</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by ID, name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th style={{ width: "40px" }}>ID</th>
                <th style={{ width: "250px" }}>Full Name</th>
                <th style={{ width: "250px" }}>Email</th>
                <th style={{ width: "250px" }}>County</th>
                <th style={{ width: "250px" }}>Country</th>
                <th style={{ width: "120px" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    <div className="loading-message">Loading Members...</div>
                  </td>
                </tr>
              ) : paginatedMembers.length > 0 ? (
                paginatedMembers.map((member, i) => (
                  <tr 
                    key={member.id || i}
                    onClick={() => handleRowClick(member)}
                  >
                    <td>{member.id}</td>
                    <td>{member.fullName}</td>
                    <td>{member.email}</td>
                    <td>{member.address.county}</td>
                    <td>{member.address.country}</td>
                    <td>
                      <span className={`status ${member.status.toLowerCase()}`}>{member.status}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    {filteredUsersCount === 0 && totalUsers > 0 
                      ? "No members match your filters" 
                      : "No members found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {!loading && filteredUsersCount > 0 && (
            <div className="pagination">
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>

      <Snackbar 
        open={snackOpen} 
        autoHideDuration={3000} 
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          severity="success"
          icon={<span style={{ fontWeight: "bold", fontSize: "18px", color:'rgba(51, 178, 51, 1' }}>✔</span>}
          sx={{
              backgroundColor: "transparent",
              backdropFilter: "blur(8px)",
              color: "#000",
              border: "2px solid rgba(51, 178, 51, 1)",
              borderRadius: "10px",
              boxShadow: "none",
              fontSize: "15px",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: '25px',
              width: '200px',
            }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ManageMember;
